/* eslint-disable indent */
import { TermFilter } from '@dofleini/query-builder';
import { Empty, Select, Spin, Tooltip } from 'antd';
import debounce from 'lodash/debounce';
import map from 'lodash/map';
import uniqBy from 'lodash/uniqBy';
import PropTypes from 'prop-types';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import useCrud from '@/hooks/useCrud';
import { useInfinite } from '@/utils/createInfiniteHook';
import createSearchService from '@/utils/createSearchService';

const { Option } = Select;
const SelectElementComponentOld = ({
  queryKey,
  service,
  value,
  onChange,
  getSelectedEntity,
  ignoreValue,
  nameToShow,
  customMethod,
  filters,
  afterLoadData,
  additionalParams,
  extraOptionData, // handle the extra information in the option for the select
  translation,
  canAdd,
  identifyValue,
  aditionalData,
  alwaysAdd,
  queryConfig,
  dropdownAction,
  clearSearch,
  setClearSearch,
  ...props
}) => {
  const { t } = useTranslation(translation || 'common');
  const name = nameToShow ? nameToShow : 'name';
  const _id = identifyValue ? identifyValue : '_id';
  const [search, setSearch] = useState('');
  const [lastOptions, setLastOptions] = useState([]);
  const [data, setData] = useState([]);
  const { create, isCreating } = useCrud(service, queryKey);
  const inputRef = useRef();
  const onSearch = useCallback((val) => setSearch(val), []);

  useEffect(() => {
    if (clearSearch) {
      onSearch('');
      if (setClearSearch) {
        setClearSearch(false);
      }
    }
  }, [clearSearch, onSearch, setClearSearch]);

  const searchService = useMemo(
    () => createSearchService(customMethod || service.search),
    [customMethod, service.search],
  );

  const beforeJoin = useCallback(
    async (pages) => {
      let data = null;
      let joint = [];
      pages?.forEach(({ data }) => {
        joint = joint.concat(data);
      });
      if (value && joint?.length !== 0) {
        const include = Array.isArray(value)
          ? value.filter(
              (e) =>
                !joint.some((item) => item[_id] === e[_id] || item[_id] === e),
            )
          : [value].filter(
              (e) =>
                !joint.some((item) => item[_id] === e[_id] || item[_id] === e),
            );

        if (include?.length) {
          const filters = {
            type: 'OR',
            filters: map(include, (value) =>
              new TermFilter({
                field: _id,
                value: value[_id] || value,
              }).toQuery(),
            ),
          };
          const { data: selectedValues } = await searchService({
            size: include?.length !== 0 ? include?.length : 1,
            search: '',
            filters: filters,
          });

          const notFaundValues = include.filter(
            (e) =>
              !selectedValues.some(
                (item) => item[_id] === e[_id] || item[_id] === e,
              ),
          );
          if (notFaundValues?.length) {
            if (Array.isArray(value)) {
              const newValue = value.filter(
                (e) =>
                  !notFaundValues.some(
                    (item) => item[_id] === e[_id] || item[_id] === e,
                  ),
              );
              onChange(newValue);
              return;
            } else {
              onChange(null);
              return;
            }
          }
          data = selectedValues?.concat(joint);
        }
      }
      setData(data ? uniqBy(data, _id) : uniqBy(joint, _id));
    },
    [_id, onChange, searchService, value],
  );

  let { isLoading, isFetching, fetchNextPage, hasNextPage } = useInfinite({
    key: [queryKey, filters, search],
    search,
    filters,
    service: customMethod || service?.search,
    additionalParams,
    beforeJoin,
    queryConfig: {
      ...queryConfig,
      size: 50,
    },
  });

  useEffect(() => {
    if (afterLoadData) {
      afterLoadData(data);
      return data;
    }
  }, [afterLoadData, data]);

  const onScroll = useCallback(
    (event) => {
      const target = event.target;
      if (
        !(isLoading && isFetching && isCreating) &&
        target.scrollTop + target.offsetHeight >= target.scrollHeight - 50 &&
        hasNextPage
      ) {
        fetchNextPage();
      }
    },
    [isLoading, isFetching, isCreating, hasNextPage, fetchNextPage],
  );

  const options = useMemo(() => {
    let elements = data?.map((e) => e) || null;
    if (elements && !isLoading) {
      if (ignoreValue) {
        if (typeof ignoreValue === 'string') {
          elements = elements.filter((elem) => elem[_id] !== ignoreValue);
        } else if (Array.isArray(ignoreValue)) {
          elements = data.filter(
            (elem) => !ignoreValue.some((item) => item === elem[_id]),
          );
        }
      }
      const opts = elements.map((item) => {
        return {
          value: item[_id],
          title: item[name],
          key: item[_id],
          label: item[name],
        };
      });
      setLastOptions(opts);
      return opts;
    }
    setLastOptions((lastOptions) => lastOptions);
    return [];
  }, [data, isLoading, ignoreValue, _id, name]);

  const handlerBuildOptions = useCallback(
    (item) => {
      const ExtraOptionData = extraOptionData;

      const extraData = data.find((element) => item?.value === element?._id);

      return (
        <Option key={item.value} value={item.value} title={item.title}>
          <div>
            <Tooltip title={item.label || item.title}>{item.title}</Tooltip>
            {extraOptionData && (
              <ExtraOptionData data={{ ...item, ...extraData }} />
            )}
          </div>
        </Option>
      );
    },
    [data, extraOptionData],
  );

  const customOnChange = useCallback(
    (value, option, create) => {
      const selected = data.filter((item) => item[_id] === value)[0];
      getSelectedEntity && getSelectedEntity(selected || option);
      if (create === 'create') {
        setData(data.concat([option]));
      }
      onChange && onChange(value, option);
    },
    [_id, data, getSelectedEntity, onChange],
  );

  const onCreate = useCallback(
    async (value) => {
      try {
        const { data } = await create({ [name]: value, ...aditionalData });
        setSearch('');
        inputRef.current?.blur();
        customOnChange(data?.id, data, 'create');
      } catch (e) {
        console.error(e);
      }
    },
    [create, aditionalData, customOnChange, name],
  );

  const addCmp = useMemo(() => {
    return (
      <div className="cursor-pointer" onClick={() => onCreate(search)}>
        <span className="mr-1">{t('add')}</span>
        <span className="text-primary font-bold">{search}</span>
      </div>
    );
  }, [onCreate, search, t]);

  return (
    <Select
      ref={inputRef}
      optionFilterProp={'title'}
      showSearch
      filterOption={false}
      allowClear={true}
      notFoundContent={
        <>
          {(canAdd && search) || (alwaysAdd && search) ? (
            addCmp
          ) : isLoading || isFetching ? (
            <div className="flex items-center justify-center">
              <Spin size="small" />
            </div>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </>
      }
      {...props}
      onSearch={debounce(onSearch, 250)}
      loading={isLoading || isFetching || isCreating}
      value={
        isLoading
          ? (props.mode === 'multiple' ? [] : undefined)
          : value || (props.mode === 'multiple' ? [] : null)
      }
      onChange={customOnChange}
      onClear={() => onChange && onChange(null)}
      onPopupScroll={onScroll}
      style={{ minWidth: '250px' }}
      dropdownRender={(menu) => (
        <>
          {menu}
          {alwaysAdd && search && !isFetching && options.length ? addCmp : null}
          {!isFetching && dropdownAction && dropdownAction}
        </>
      )}
    >
      {!isFetching
        ? options.map(handlerBuildOptions)
        : [
            ...lastOptions.map(handlerBuildOptions),
            <Option key="loading" value="loading-item" disabled>
              <div
                key="loading"
                className="flex items-center justify-center w-full"
              >
                <span className="mr-4">
                  <Spin size="small" />
                </span>
                Loading...
              </div>
            </Option>,
          ]}
    </Select>
  );
};

export default memo(SelectElementComponentOld);

SelectElementComponentOld.propTypes = {
  additionalParams: PropTypes.object,
  customMethod: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  filters: PropTypes.object,
  ignoreValue: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  includeValue: PropTypes.any,
  mode: PropTypes.string,
  nameToShow: PropTypes.string,
  canAdd: PropTypes.bool,
  translation: PropTypes.string,
  getSelectedEntity: PropTypes.func,
  onChange: PropTypes.func,
  afterLoadData: PropTypes.func,
  queryKey: PropTypes.string,
  identifyValue: PropTypes.string,
  aditionalData: PropTypes.object,
  extraOptionData: PropTypes.any,
  service: PropTypes.object,
  value: PropTypes.any,
  alwaysAdd: PropTypes.bool,
  queryConfig: PropTypes.object,
  dropdownAction: PropTypes.any,
  clearSearch: PropTypes.bool,
  setClearSearch: PropTypes.func,
};

SelectElementComponentOld.defaultProps = {
  filters: {},
  aditionalData: {},
};
